import React, { useState, useEffect } from 'react';
import { Link, useParams, useHistory, Prompt } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Form, Alert, Spinner } from 'react-bootstrap';
import Swal from 'sweetalert2';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import PageHeader from '../../../layouts/PageHeader';
import ModernContractDetailsComponent from './ModernContractDetailsComponent';
import {
  ArrowLeft,
  Save,
  X,
  AlertTriangle,
    FileText
} from 'lucide-react';
import './employee-form.css';

const EditContract = () => {
  const { companyId, id, contractId } = useParams();
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessfullySubmitted, setIsSuccessfullySubmitted] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const formik = useFormik({
    initialValues: {
      startdate: '',
      enddate: '',
      employmenttype: '',
      salary: '',
      salaryperiod: 'Advance',
      workweek: '40',
      employmentextent: '',
      parttimePercentage: '',
      department_id: '',
      position_id: '',
      vacation_days: '25',
      notice_period_weeks: '4',
      probation_period_months: '',
      taxtable: '33',
      taxcolumn: '1',
      workplace: 'Other',
      workplaceAddress: '',
      workplaceCity: '',
      otherterms: '',
      accordingtocollectiveagreement: true,
      high_risk_protection: false,
      worker_category: 'tjansteman',
    },
    validationSchema: Yup.object({
      startdate: Yup.date().required('Startdatum är obligatoriskt'),
      enddate: Yup.date().nullable().min(Yup.ref('startdate'), 'Slutdatum måste vara efter startdatum'),
      employmenttype: Yup.string().required('Anställningsform är obligatoriskt'),
      salary: Yup.number()
        .typeError('Lön måste vara en siffra')
        .positive('Lön måste vara större än 0')
        .required('Lön är obligatoriskt'),
      salaryperiod: Yup.string().required('Löneperiod är obligatoriskt'),
      workweek: Yup.number()
        .typeError('Arbetsvecka måste vara en siffra')
        .min(1, 'Arbetsvecka måste vara minst 1 timme')
        .max(60, 'Arbetsvecka kan inte överstiga 60 timmar')
        .required('Arbetsvecka är obligatoriskt'),
      employmentextent: Yup.string().required('Omfattning är obligatoriskt'),
      department_id: Yup.string().required('Avdelning är obligatoriskt'),
      position_id: Yup.string().required('Position är obligatoriskt'),
      vacation_days: Yup.number()
        .typeError('Semesterdagar måste vara en siffra')
        .min(0, 'Semesterdagar kan inte vara negativt')
        .max(50, 'Semesterdagar kan inte överstiga 50')
        .required('Semesterdagar är obligatoriskt'),
      notice_period_weeks: Yup.number()
        .typeError('Uppsägningstid måste vara en siffra')
        .min(0, 'Uppsägningstid kan inte vara negativt')
        .max(24, 'Uppsägningstid kan inte överstiga 24 veckor')
        .required('Uppsägningstid är obligatoriskt'),
      probation_period_months: Yup.number()
        .typeError('Provanställning måste vara en siffra')
        .min(0, 'Provanställning kan inte vara negativt')
        .max(12, 'Provanställning kan inte överstiga 12 månader')
        .nullable(),
      taxtable: Yup.string().required('Skattetabell är obligatoriskt'),
      taxcolumn: Yup.string().required('Skattekollumn är obligatoriskt'),
      workplace: Yup.string().required('Tjänsteställe är obligatoriskt'),
      workplaceAddress: Yup.string().required('Arbetsplatsens adress är obligatorisk'),
      workplaceCity: Yup.string().required('Arbetsplatsens ort är obligatorisk'),
      parttimePercentage: Yup.number()
        .nullable()
        .when('employmentextent', {
          is: 'Part-time',
          then: Yup.number()
            .required('Ange procent för deltid')
            .min(1, 'Procenten måste vara minst 1%')
            .max(99, 'Procenten kan inte överstiga 99%'),
          otherwise: Yup.number().nullable(),
        }),
      otherterms: Yup.string(),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setShowValidation(true);

      try {
        const formattedValues = {
          ...values,
          workplace: {
            name: values.workplace,
            address: values.workplaceAddress,
            city: values.workplaceCity
          }
        };

        delete formattedValues.workplaceAddress;
        delete formattedValues.workplaceCity;

        // Convert string values to appropriate types
        formattedValues.salary = parseFloat(formattedValues.salary);
        formattedValues.workweek = parseFloat(formattedValues.workweek);
        formattedValues.vacation_days = parseInt(formattedValues.vacation_days);
        formattedValues.notice_period_weeks = parseInt(formattedValues.notice_period_weeks);
        
        if (formattedValues.probation_period_months) {
          formattedValues.probation_period_months = parseInt(formattedValues.probation_period_months);
        }

        if (formattedValues.parttimePercentage) {
          formattedValues.parttimePercentage = parseFloat(formattedValues.parttimePercentage);
        }

        const updateEmployeeContractApiUrl = getApiUrl(
          `${companyId}/hr/employees/${id}/contracts/${contractId}`
        );

        const response = await axios.put(
          updateEmployeeContractApiUrl,
          formattedValues,
          { withCredentials: true }
        );

        if (response.data.status === 'success') {
          setIsSuccessfullySubmitted(true);
          
          // Build warning message if there are adjustments or warnings
          let warningHtml = '';
          if (response.data.adjustedContracts && response.data.adjustedContracts.length > 0) {
            warningHtml += '<p class="text-start mb-2"><strong>Justerade kontrakt:</strong></p><ul class="text-start">';
            response.data.adjustedContracts.forEach(adj => {
              warningHtml += `<li>Kontrakt från ${adj.old_valid_from} har fått sitt slutdatum ändrat till ${adj.new_valid_to}</li>`;
            });
            warningHtml += '</ul>';
          }
          
          if (response.data.warnings && response.data.warnings.length > 0) {
            warningHtml += '<p class="text-start mb-2"><strong>Varningar:</strong></p><ul class="text-start">';
            response.data.warnings.forEach(warning => {
              warningHtml += `<li>${warning}</li>`;
            });
            warningHtml += '</ul>';
          }

          if (warningHtml) {
            await Swal.fire({
              icon: 'success',
              title: 'Kontraktet har uppdaterats!',
              html: warningHtml,
              confirmButtonText: 'OK'
            });
          } else {
            await Swal.fire({
              icon: 'success',
              title: 'Sparat!',
              text: 'Kontraktet har uppdaterats framgångsrikt.',
              timer: 1500
            });
          }
          
          history.push(`/${companyId}/employee/${id}`);
        }
      } catch (error) {
        console.error('Error updating contract:', error);
        const errorMessage = error.response?.data?.message || 'Ett fel inträffade vid uppdatering av kontraktet.';
        await Swal.fire({
          icon: 'error',
          title: 'Fel',
          text: errorMessage
        });
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Load contract data
  const loadContract = async () => {
    try {
      const getContractsApiUrl = getApiUrl(`${companyId}/hr/employees/${id}/contracts`);
      const result = await axios.get(getContractsApiUrl, { withCredentials: true });
      const contract = result.data.find(c => c.contract_id === contractId);
      
      if (!contract) {
        throw new Error('Contract not found');
      }

      // Populate form with contract data
      formik.setValues({
        startdate: contract.valid_from,
        enddate: contract.valid_to || '',
        employmenttype: contract.employment_type || contract.employmenttype,
        salary: contract.base_salary || contract.salary,
        salaryperiod: contract.salary_period,
        workweek: contract.work_week_hours,
        employmentextent: contract.employmentextent,
        parttimePercentage: contract.work_week_hours ? (contract.work_week_hours / 40 * 100).toString() : '',
        department_id: contract.department_id || '',
        position_id: contract.position_id || '',
        vacation_days: contract.vacation_days || 25,
        notice_period_weeks: contract.notice_period_weeks || 4,
        probation_period_months: contract.probation_period_months || '',
        taxtable: contract.taxtable || '33',
        taxcolumn: contract.taxcolumn || '1',
        workplace: 'Other',
        workplaceAddress: contract.worklocation_address || '',
        workplaceCity: contract.worklocation_city || '',
        otherterms: contract.otherterms || '',
        accordingtocollectiveagreement: contract.iscollectiveagreement || true,
        high_risk_protection: !!contract.high_risk_protection,
        worker_category: contract.worker_category || 'tjansteman',
      });
    } catch (error) {
      console.error('Error loading contract:', error);
      setLoadError('Kunde inte ladda kontraktsdata.');
    }
  };

  // Load employee data
  const loadEmployee = async () => {
    try {
      const getEmployeeApiUrl = getApiUrl(`${companyId}/hr/employees/${id}`);
      const result = await axios.get(getEmployeeApiUrl, { withCredentials: true });
      setEmployee(result.data);
    } catch (error) {
      console.error('Error loading employee:', error);
    }
  };

  // Load departments
  const loadDepartments = async () => {
    try {
      const getDepartmentsApiUrl = getApiUrl(`${companyId}/hr/departments`);
      const result = await axios.get(getDepartmentsApiUrl, { withCredentials: true });
      if (result.data.status === 'success') {
        setDepartments(result.data.data);
      }
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  // Load positions
  const loadPositions = async () => {
    try {
      const getPositionsApiUrl = getApiUrl(`${companyId}/hr/positions`);
      const result = await axios.get(getPositionsApiUrl, { withCredentials: true });
      if (result.data.status === 'success') {
        setPositions(result.data.data);
      }
    } catch (error) {
      console.error('Error loading positions:', error);
    }
  };

  // Load all initial data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        loadEmployee(), 
        loadDepartments(), 
        loadPositions(),
        loadContract()
      ]);
      setLoading(false);
    };
    loadData();
  }, [companyId, id, contractId]);

  // Handle form dirty state for navigation warnings
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (formik.dirty && !isSuccessfullySubmitted) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formik.dirty, isSuccessfullySubmitted]);

  const handleCancel = async () => {
    if (formik.dirty && !isSuccessfullySubmitted) {
      const result = await Swal.fire({
        title: 'Är du säker?',
        text: 'Du har osparade ändringar som kommer att gå förlorade!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ja, avbryt!',
        cancelButtonText: 'Nej, fortsätt redigera'
      });

      if (result.isConfirmed) {
        history.push(`/${companyId}/employee/${id}`);
      }
    } else {
      history.push(`/${companyId}/employee/${id}`);
    }
  };

  const isFormReadyToSubmit = () => {
    const requiredFields = [
      'startdate', 'employmenttype', 'salary', 'salaryperiod', 'workweek',
      'employmentextent', 'department_id', 'position_id', 'vacation_days',
      'notice_period_weeks', 'taxtable', 'taxcolumn', 'workplace',
      'workplaceAddress', 'workplaceCity'
    ];

    const allFieldsFilled = requiredFields.every(field => {
      const value = formik.values[field];
      return value !== null && value !== undefined && value !== '';
    });

    const partTimeValid = formik.values.employmentextent !== 'Part-time' ||
      (formik.values.parttimePercentage && formik.values.parttimePercentage > 0);

    const noErrors = Object.keys(formik.errors).length === 0;

    return allFieldsFilled && partTimeValid && noErrors;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2 text-muted">Laddar kontraktsdata...</div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="container-fluid px-0">
        <Alert variant="danger" className="mb-4">
          <AlertTriangle size={20} className="me-2" />
          {loadError}
        </Alert>
      </div>
    );
  }

  return (
    <>
      <Prompt
        when={formik.dirty && !isSuccessfullySubmitted}
        message="Du har osparade ändringar. Är du säker på att du vill lämna?"
      />

      <div className="emp-form-page">
        <PageHeader
          backTo={{ to: `/${companyId}/employee/${id}`, label: 'Tillbaka till anställd' }}
          icon={FileText}
          title="Redigera kontrakt"
        />

        <form onSubmit={formik.handleSubmit}>
          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  <ModernContractDetailsComponent 
                    formik={formik} 
                    companyId={companyId}
                    showValidation={showValidation}
                  />
                  <div className="form-actions">
                    <button
                      type="button"
                      className="ll-btn ll-btn--secondary"
                      onClick={handleCancel}
                      disabled={isSubmitting}
                    >
                      Avbryt
                    </button>
                    <button
                      type="submit"
                      className="ll-btn ll-btn--primary"
                      disabled={isSubmitting || !isFormReadyToSubmit()}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          Sparar...
                        </>
                      ) : (
                        'Spara ändringar'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default EditContract;

